import React, { useCallback, useState } from 'react';
import { useIntl } from "react-intl";
import { Paragraph, SceneTemplate, SceneTemplateCreate } from "@/model";
import { useCreateTemplate, useTemplates } from '@/query/template';
import { useModal, useScenarioId } from "@/hook";
import Button from "@/component/Button";
import { TextInputGroup } from "@/component/input";
import { Modal } from "@/component/modal";
import { successToast, errorToast } from '@/component/Toast';


export const TEMPLATE_CONVERT_MODAL_ID = "template-convert-Modal-id";

interface TemplateConvertModalProps {
    onSubmit: (pId: number, template: SceneTemplate) => void;
}

const TemplateConvertModal: React.FC<TemplateConvertModalProps> = (props) => {
    const { onSubmit } = props;

    const intl = useIntl();
    const modal = useModal();
    const scenarioId = useScenarioId();
    const paragraph: Paragraph = modal.getData(TEMPLATE_CONVERT_MODAL_ID);

    const [name, setName] = useState<string>("");
    const [isValid, setIsValid] = useState<boolean>(false);

    const { data: templates } = useTemplates(scenarioId);
    const { mutateAsync: createTemplate, isPending: isCreating } = useCreateTemplate(scenarioId);

    const checkNameUsed = useCallback((name: string) => {
        return templates?.some(template => template.name === name.trim()) ?? false;
    }, [templates]);

    const submit = async () => {
        const form: SceneTemplateCreate = {
            name: name.trim(),
            paragraphs: [paragraph]
        };
        if (!form.name || checkNameUsed(form.name)) {
            return;
        }
        createTemplate(form)
            .then(newTemplate => {
                successToast(intl.formatMessage({ id: "t001" }));
                onSubmit(paragraph.id, newTemplate);
                modal.close(TEMPLATE_CONVERT_MODAL_ID);
            })
            .catch(e => {
                if (checkNameUsed(form.name)) {
                    errorToast(intl.formatMessage({ id: "i03007" }));
                    return;
                }
                errorToast(intl.formatMessage({ id: "t002" }));
            });
    };

    const onChangeName = (value: string) => {
        setName(value);
        setIsValid(!!value.trim() && !checkNameUsed(value.trim()));
    };

    const onClickCancel = (e: React.MouseEvent) => {
        e.preventDefault();
        modal.close(TEMPLATE_CONVERT_MODAL_ID);
    };

    const onClickConfirm = async (e: React.MouseEvent) => {
        e.preventDefault();
        await submit();
    };

    return (
        <Modal id={TEMPLATE_CONVERT_MODAL_ID}
               size="sm"
               title={intl.formatMessage({ id: "i03001" })}
               onClose={() => setIsValid(false)}
        >
            <div className="modal-body">
                <div className="modal-description">
                    <TextInputGroup required
                                    useDirty
                                    value={name}
                                    label={intl.formatMessage({ id: "b0021" })}
                                    placeholder={intl.formatMessage({ id: "i03003" })}
                                    invalid={!isValid}
                                    invalidText={checkNameUsed(name.trim()) ? intl.formatMessage({ id: "i03007" }) : undefined}
                                    onChange={onChangeName}
                    />
                </div>

                <div className="modal-btn-group">
                    <Button size="sm"
                            variant="secondary"
                            onClick={onClickCancel}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button size="sm"
                            variant="primary"
                            disabled={isCreating || !isValid}
                            onClick={onClickConfirm}
                    >
                        {intl.formatMessage({ id: "b0001" })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default TemplateConvertModal;
